import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import cmsCategoryObject from '@/entities/cmsCategory/interface'
import cmsPageObject from '@/entities/cmsPage/interface'

import {
  ALL_CMS_QUERY,
  SINGLE_CMS_QUERY,
  CMS_DEPENDENTS_QUERY,
  CREATE_CMS_MUTATION,
  UPDATE_CMS_MUTATION,
  DELETE_CMS_MUTATION,
  FORCE_DELETE_CMS_MUTATION,
  ADD_CMS_CATEGORIES_MUTATION,
  REMOVE_CMS_CATEGORIES_MUTATION,
  CREATE_CMS_CATEGORIES_MUTATION,
  DELETE_CMS_CATEGORIES_MUTATION,
  UPDATE_CMS_CATEGORIES_ORDER_MUTATION,
  ADD_CMS_PAGES_MUTATION,
  REMOVE_CMS_PAGES_MUTATION,
  CREATE_CMS_PAGES_MUTATION,
  DELETE_CMS_PAGES_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const tenantCmsRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_CMS_QUERY, variables).then((data: any) => {
      return hydrateObject(data.cms)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_CMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.cms)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_CMS_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.cms),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_CMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.cms),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(CMS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([CMS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_CMS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.cms)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_CMS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.cms)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_CMS_MUTATION, variables).then((data: any) => {
      return data.deleteTenantCms
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_CMS_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTenantCms
    })
  },

  assignCategories: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        categoryIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CMS_CATEGORIES_MUTATION, variables)
  },

  unassignCategories: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        categoryIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CMS_CATEGORIES_MUTATION, variables)
  },

  createCategories: async (id: string, items: Array<cmsCategoryObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        categories: items,
      },
    }
    return graphQLClient.request(CREATE_CMS_CATEGORIES_MUTATION, variables)
  },

  deleteCategories: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        categoryIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CMS_CATEGORIES_MUTATION, variables)
  },

  updateCategoriesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        categoryIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_CMS_CATEGORIES_ORDER_MUTATION, variables)
  },

  assignPages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CMS_PAGES_MUTATION, variables)
  },

  unassignPages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CMS_PAGES_MUTATION, variables)
  },

  createPages: async (id: string, items: Array<cmsPageObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pages: items,
      },
    }
    return graphQLClient.request(CREATE_CMS_PAGES_MUTATION, variables)
  },

  deletePages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CMS_PAGES_MUTATION, variables)
  },
}

export default tenantCmsRepository
